import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  Text,
} from '@southfields-digital/mpxlive-components';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ActiveGraphics, { ActiveGraphicsHeader } from 'src/components/ActiveGraphics';
import BundledGraphics from 'src/components/BundledGraphics';
import { StatusDescription } from 'src/components/GoLive';
import { getMappedCanvases } from 'src/components/GoLive/utils';
import MatchControl from 'src/components/MatchControl';
import QuickControl from 'src/components/QuickControl/QuickControl';
import ToggleButtons from 'src/components/ToggleButtons/ToggleButtons';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { StateType } from 'src/redux/reducers';

import LayoutSimplified from './Simplified';

export default function LayoutDefault() {
  const { rundownId } = useParams<{ rundownId: string }>();
  const { data, outputs } = useSelector((state: StateType) => state.rundown.detail);
  const { bundledPlaylistGraphics } = useSelector(
    (state: StateType) => state.bundledPlaylistGraphic
  );

  const [selectedCanvas, setSelectedCanvas] = useState<RundownTemplateCanvas | undefined>();
  const [showMatchControl, setShowMatchControl] = useState<boolean>(false);
  const [showBundledGraphics, setShowBundledGraphics] = useState<boolean>(false);

  useEffect(() => {
    if (data?.isLive && !selectedCanvas) {
      const activeCanvas = data.canvases.find((canvas) => canvas.output && canvas.graphics.length);
      if (activeCanvas) {
        setSelectedCanvas(activeCanvas);
      }
    }
  }, [data]);

  if (!data || !rundownId) return null;

  return (
    <LayoutSimplified>
      <PanelResizeHandle />

      <Panel defaultSize={25}>
        <PanelGroup direction="vertical">
          <StatusDescription
            rundown={data}
            mappedCanvases={getMappedCanvases(data.canvases, outputs)}
            isLive={data.isLive}
            className="border-b-[4px] border-[#151621]"
          />
          {data.isLive && (
            <>
              <Panel
                defaultSize={37}
                headerSize="large"
                headerStyle={{ padding: 0 }}
                header={
                  <ToggleButtons
                    className="w-full justify-center px-2 -mt-0.5"
                    items={data.canvases.filter((canvas) => canvas.graphics.length)}
                    selectedItem={selectedCanvas}
                    onClickItem={(canvas) => setSelectedCanvas(canvas)}
                  />
                }
              >
                {selectedCanvas?.output?.liveUrl ? (
                  <ViewWindow name="PGM" src={selectedCanvas.output.liveUrl} liveMode />
                ) : (
                  <Text
                    as="p"
                    className="h-full !text-sm !text-gray-500 p-2 text-center content-center"
                  >
                    No output configured for this canvas
                  </Text>
                )}
              </Panel>
              <PanelResizeHandle />
            </>
          )}
          <Panel
            defaultSize={data.isLive ? 32.25 : 35}
            headerSize="large"
            header={
              <ToggleButtons
                items={[
                  { id: 'quickControls', name: 'Quick Control' },
                  {
                    id: 'matchControls',
                    name: 'Match Control',
                    visible: Boolean(data.matchControl),
                  },
                  {
                    id: 'bundledGraphics',
                    name: 'Bundle Control',
                    visible: Boolean(bundledPlaylistGraphics?.length),
                  },
                ]}
                selectedItem={
                  showMatchControl
                    ? { id: 'matchControls', name: 'Match Control' }
                    : showBundledGraphics
                      ? { id: 'bundledGraphics', name: 'Bundle Control' }
                      : { id: 'quickControls', name: 'Quick Control' }
                }
                onClickItem={(item) => {
                  setShowMatchControl(item.id === 'matchControls');
                  setShowBundledGraphics(item.id === 'bundledGraphics');
                }}
              />
            }
          >
            {showMatchControl ? (
              <MatchControl rundownId={rundownId} />
            ) : showBundledGraphics ? (
              <BundledGraphics />
            ) : (
              <QuickControl className="!p-4 !gap-2" />
            )}
          </Panel>

          <PanelResizeHandle />

          <Panel header={<ActiveGraphicsHeader />} headerSize="large">
            <ActiveGraphics />
          </Panel>
        </PanelGroup>
      </Panel>
    </LayoutSimplified>
  );
}
